import type {
	NubeSDKDevtoolsEvent,
	NubeSDKDevtoolsHook,
	NubeSDKDevtoolsLog,
	NubeSDKDevtoolsLogRecord,
	NubeSDKDevtoolsRecord,
} from "@tiendanube/nube-sdk-internal-types";

/**
 * How many dispatches the hook keeps. Each record holds two state snapshots,
 * so the bound is what keeps a long session (or a burst of high-frequency
 * events like `page:scroll`) from growing without limit. Enough to cover the
 * page load plus recent activity, which is what a panel opened mid-session
 * needs.
 */
const DEFAULT_MAX_RECORDS = 200;

/**
 * How many logs the hook keeps. Logs are small (no state snapshots), but an
 * app logging in a loop must not grow the buffer without limit either.
 */
const DEFAULT_MAX_LOGS = 500;

/**
 * A bounded, replayable stream: stamps each entry with a monotonic `seq`,
 * keeps the latest `max` of them and replays them to late subscribers.
 */
function createReplayBuffer<Entry, Stamped extends Entry & { seq: number }>(
	max: number,
) {
	let entries: Stamped[] = [];
	const subscribers = new Set<(entry: Stamped) => void>();
	let nextSeq = 0;

	function notify(subscriber: (entry: Stamped) => void, entry: Stamped) {
		try {
			subscriber(entry);
		} catch (error) {
			// A broken panel must never break the storefront.
			console.error("NubeSDK devtools subscriber failed:", error);
		}
	}

	return {
		emit(entry: Entry) {
			const stamped = { ...entry, seq: nextSeq++ } as Stamped;

			entries.push(stamped);
			if (entries.length > max) {
				entries = entries.slice(entries.length - max);
			}

			for (const subscriber of subscribers) notify(subscriber, stamped);
		},

		entries() {
			return [...entries];
		},

		subscribe(subscriber: (entry: Stamped) => void) {
			// Replay first: a panel opened after page load gets the history and
			// the live stream through the same callback, in emission order.
			for (const entry of entries) notify(subscriber, entry);

			subscribers.add(subscriber);
			return () => {
				subscribers.delete(subscriber);
			};
		},
	};
}

export function createDevtoolsHook(
	maxRecords: number = DEFAULT_MAX_RECORDS,
	maxLogs: number = DEFAULT_MAX_LOGS,
): Required<NubeSDKDevtoolsHook> {
	const dispatches = createReplayBuffer<
		NubeSDKDevtoolsEvent,
		NubeSDKDevtoolsRecord
	>(maxRecords);
	const logs = createReplayBuffer<NubeSDKDevtoolsLog, NubeSDKDevtoolsLogRecord>(
		maxLogs,
	);

	return {
		emit: dispatches.emit,
		records: dispatches.entries,
		subscribe: dispatches.subscribe,
		emitLog: logs.emit,
		logs: logs.entries,
		subscribeLogs: logs.subscribe,
	};
}

/**
 * Resolves the devtools hook for the current page, or `undefined` when
 * devtools is not present.
 *
 * Looked up on every dispatch instead of being cached, so a panel that
 * installs its hook late (or an extension that flips the flag after the
 * runtime bundle ran) is still picked up. Two property reads is all a store
 * without devtools ever pays.
 */
export function getDevtoolsHook(): NubeSDKDevtoolsHook | undefined {
	if (typeof window === "undefined") return undefined;

	const installed = window.__NUBE_SDK_DEVTOOLS_HOOK__;
	if (installed) return installed;

	// No hook installed by the extension: create one only when the extension
	// announced itself, so the buffer exists from the very first event even if
	// the panel connects much later.
	if (window.__NUBE_DEVTOOLS_EXTENSION__ !== true) return undefined;

	const hook = createDevtoolsHook();
	window.__NUBE_SDK_DEVTOOLS_HOOK__ = hook;
	return hook;
}
